import React from 'react';
import gql from 'graphql-tag';
import { useQuery, useMutation } from '@apollo/react-hooks';
import { Formik } from 'formik';
import * as Yup from 'yup';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    TextField,
    CircularProgress
} from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import PasswordField from '../../components/PasswordField/PasswordField';
import { getGraphQLErrorMessage } from '../../utils/graphqlError';

const FETCH_SCHOOLS = gql`
    {
        fetchSchools {
            id name
        }
    }
`;

const PREVIOUS_ACCESS_BULLETINS = gql`
    mutation previousAccessBulletins($login: String!, $password: String!, $school_id: Int!) {
        previousAccessBulletins(login: $login, password: $password, school_id: $school_id)
    }
`;

const initialValues = { school_id: '', login: '', password: '' };

const validationSchema = Yup.object().shape({
    school_id: Yup.number().typeError('Informe a unidade').required('Informe a unidade'),
    login: Yup.string().trim().required('Informe a matrícula'),
    password: Yup.string().required('Informe a senha')
});

const fieldError = (formik, field) => (formik.touched[field] && formik.errors[field]) || undefined;

/**
 * Transfere os boletins de um acesso antigo (outra matrícula) para a conta
 * logada — mesma função do "Deseja transferir suas notas" do app.
 */
const TransferBulletinsDialog = ({ open, onClose, onSuccess, onError }) => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('xs'));

    const { data: schoolsData } = useQuery(FETCH_SCHOOLS, { skip: !open });
    const [transferBulletins] = useMutation(PREVIOUS_ACCESS_BULLETINS);

    const handleSubmit = async (values, { setSubmitting, resetForm }) => {
        try {
            await transferBulletins({
                variables: {
                    login: values.login.trim(),
                    password: values.password,
                    school_id: Number(values.school_id)
                }
            });
            resetForm();
            onSuccess();
        } catch (error) {
            onError(getGraphQLErrorMessage(error, 'Não foi possível transferir as notas. Tente novamente.'));
        } finally {
            setSubmitting(false);
        }
    };

    const schools = (schoolsData && schoolsData.fetchSchools) || [];

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" fullScreen={isSmallScreen} aria-labelledby="transfer-bulletins-title">
            <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
                {formik => (
                    <form onSubmit={formik.handleSubmit} noValidate>
                        <DialogTitle id="transfer-bulletins-title">Informe seu acesso anterior</DialogTitle>
                        <DialogContent>
                            <DialogContentText>
                                Use seu acesso antigo para trazer os boletins de outra conta para esta.
                            </DialogContentText>
                            <TextField
                                select
                                SelectProps={{ native: true }}
                                label={schoolsData ? 'Unidade' : 'Carregando unidades...'}
                                name="school_id"
                                value={formik.values.school_id}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                disabled={!schoolsData}
                                error={Boolean(fieldError(formik, 'school_id'))}
                                helperText={fieldError(formik, 'school_id')}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                                margin="normal"
                            >
                                <option value="" />
                                {schools.map(school => <option key={school.id} value={school.id}>{school.name}</option>)}
                            </TextField>
                            <TextField
                                label="Matrícula"
                                name="login"
                                value={formik.values.login}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={Boolean(fieldError(formik, 'login'))}
                                helperText={fieldError(formik, 'login')}
                                autoComplete="off"
                                fullWidth
                                margin="normal"
                            />
                            {/* autoComplete off: o navegador não deve preencher com a senha da conta atual. */}
                            <PasswordField
                                label="Senha"
                                name="password"
                                value={formik.values.password}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={Boolean(fieldError(formik, 'password'))}
                                helperText={fieldError(formik, 'password')}
                                autoComplete="off"
                                fullWidth
                                margin="normal"
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={onClose} disabled={formik.isSubmitting}>Cancelar</Button>
                            <Button type="submit" color="primary" variant="contained" disabled={formik.isSubmitting}>
                                {formik.isSubmitting && <CircularProgress size={14} style={{ marginRight: 8 }} />}
                                Confirmar
                            </Button>
                        </DialogActions>
                    </form>
                )}
            </Formik>
        </Dialog>
    );
};

export default TransferBulletinsDialog;
